import { cloneDeep, get } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { checkIsHorizontalMultiLine, TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    MaterialIdsType,
    TextWritingType,
    type EditingRange,
    type ILayerProps,
    type ITextLayerProps,
} from '@/version-types';
import {
    getLayerFontFamilyIds,
    pushHistory,
    RichTextProcessor,
    updateGroupLayer,
    updateLayer,
    validateInputLayer,
} from '../utils';
import { type IShapeText } from './shape-text';

export interface IFontFamily {
    /**
     * 设置字体
     * @param layerID - 图层 ID
     * @param fontFamily - 字体名称
     * @param materialID - 字体素材 ID
     * @param options - 配置项
     * @returns
     */
    __setFontFamily(
        layerID: string,
        fontFamily: string,
        options?: { range?: EditingRange | null },
    ): Promise<{ layer: ILayerProps; range: EditingRange | null } | void>;
}

export class FontFamily implements IFontFamily {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    // private updateMaterialIDs(layer: ILayerProps, materialID: string) {
    //     const layerData = layer.data as ITextLayerProps;
    //     const { textContents = [] } = layerData;
    //     const prevMaterialIDMap = layer.materialIds;

    //     if (textContents.length === 0) {
    //         const nextMaterialIDMap = prevMaterialIDMap ? cloneDeep(prevMaterialIDMap) : {};
    //         nextMaterialIDMap[MaterialIdsType.FONT_FAMILY] = [materialID];
    //         return nextMaterialIDMap;
    //     }

    //     const fontNames = new Set(textContents.map((item) => item.fontFamily));
    //     const fontMaterialIDs = this.framework
    //         .getFontList()
    //         .filter((item) => fontNames.has(item['font_name']) && item['material_id'])
    //         .map((item) => `${item['material_id']}`);

    //     const nextMaterialIDMap = prevMaterialIDMap ? cloneDeep(prevMaterialIDMap) : {};
    //     nextMaterialIDMap[MaterialIdsType.FONT_FAMILY] = fontMaterialIDs;
    //     return nextMaterialIDMap;
    // }

    private updateMaterialIDs(layer: ILayerProps) {
        const fontFamilyIds = getLayerFontFamilyIds(this.framework, layer);

        const prevMaterialIDMap = layer.materialIds;
        const nextMaterialIDMap = prevMaterialIDMap ? cloneDeep(prevMaterialIDMap) : {};

        nextMaterialIDMap[MaterialIdsType.FONT_FAMILY] = fontFamilyIds;
        return nextMaterialIDMap;
    }

    private calcLayout(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as ITextLayerProps;
        const { writingMode, fontSize, lineSpacing } = layerData;

        const measureOptions = {
            keepConstantWidth: false,
            keepConstantHeight: false,
        };

        // 保持宽度不变条件：
        // 1. writingMode 为横向（必须）
        // 2. 图层已设置 `__fixedDirectionSize` 为 true 或 当前是横向多行文本

        if (
            writingMode === TextWritingType.HORIZONTAL_TB &&
            (get(layer, '__fixedDirectionSize', false) ||
                checkIsHorizontalMultiLine(layerHeight, fontSize, lineSpacing))
        ) {
            measureOptions.keepConstantWidth = true;
        }

        const textSize = TextMeasurer.measure(layer, measureOptions);
        const nextLayerWidth = textSize.width > layerWidth ? textSize.width : layerWidth;
        const nextLayerHeight = textSize.height;

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    private async set(
        prevLayer: ILayerProps,
        fontFamily: string,
        options: { range?: EditingRange | null; loading?: boolean; activate?: boolean; writeHistory?: boolean } = {},
    ) {
        const { loading = false, activate = true, writeHistory = false } = options;
        const range = options.range || null;

        const prevLayerData = prevLayer.data as ITextLayerProps;

        let nextLayerData: ITextLayerProps = { ...prevLayerData };
        let nextRange: EditingRange | null = range;

        if (range && !range.isCollapsed) {
            const result = RichTextProcessor.process(nextLayerData, range, {
                key: 'fontFamily',
                value: fontFamily,
            });
            nextRange = result.range;
            nextLayerData = { ...nextLayerData, textContents: result.textContents };
        } else {
            nextLayerData = { ...nextLayerData, fontFamily };

            const { textContents: prevTextContents = [] } = nextLayerData;
            if (prevTextContents.length > 0) {
                const nextTextContents = prevTextContents.map((item) => ({
                    ...item,
                    fontFamily,
                }));
                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            }
        }

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        // ATTENTION: 该方法会改变 nextLayer 的 runtime
        await this.framework.registerFont(nextLayer, fontFamily, loading);

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            const { width, height } = this.calcLayout(nextLayer);
            nextLayer = { ...nextLayer, width, height };
        } else {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        const nextMaterialIDMap = this.updateMaterialIDs(nextLayer);
        nextLayer = { ...nextLayer, materialIds: nextMaterialIDMap };

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].family);

        return {
            layer: nextLayer,
            range: nextRange,
        };
    }

    async __setFontFamily(layerID: string, fontFamily: string, options: { range?: EditingRange | null } = {}) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            this.framework.setLoading(prevLayer.id);

            const results = await Promise.all(
                children.map((child) => this.set(child, fontFamily, { activate: false, loading: false })),
            );
            const layers = results.map((result) => (result ? result.layer : null)).filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER].family,
                update: true,
                writeHistory: true,
            });

            this.framework.removeLoading(prevLayer.id);

            return { layer: nextLayer, range: null };
        }

        const { range = null } = options;
        return this.set(prevLayer, fontFamily, { range, loading: true, activate: true, writeHistory: true });
    }
}
